import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector } from 'react-redux';
import { selectAllWires } from '../../../redux/wireSlice';

export function useFixtureGizmo(scene, camera, domElement) {
    const selectedObjectIds = useSelector((state) => state.selection.selectedObjectIds);
    const wires = useSelector(selectAllWires);
    const gizmoGroupRef = useRef(null);
    const isMoving = useRef(false);
    const selectedGizmo = useRef(null);
    const raycaster = useRef(new THREE.Raycaster());
    const plane = useRef(new THREE.Plane());
    const offset = useRef(new THREE.Vector3());

    // ✅ Dynamic Scaling Effect
    useEffect(() => {
        if (!scene || !camera) return;

        let animationFrameId;

        const updateScale = () => {
            if (gizmoGroupRef.current) {
                gizmoGroupRef.current.children.forEach((gizmo) => {
                    let scale = 1;
                    if (camera.isOrthographicCamera) {
                        scale = 10 / camera.zoom; // 🟢 GIZMO BOYUTU (Orthographic): Buradaki 10 değerini değiştirerek boyutu ayarlayabilirsiniz
                    } else {
                        const distance = camera.position.distanceTo(gizmo.position);
                        scale = distance * 0.01; // 🟢 GIZMO BOYUTU (Perspective): Buradaki 0.01 değerini değiştirerek boyutu ayarlayabilirsiniz
                    }
                    gizmo.scale.set(scale, scale, scale);
                });
            }
            animationFrameId = requestAnimationFrame(updateScale);
        };

        updateScale();

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [scene, camera]);

    // 1. Render Gizmos
    useEffect(() => {
        if (!scene) return;

        // Clear old gizmos
        if (gizmoGroupRef.current) {
            scene.remove(gizmoGroupRef.current);
            gizmoGroupRef.current.traverse(child => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) child.material.dispose();
            });
            gizmoGroupRef.current = null;
        }

        if (selectedObjectIds.length === 0) return;

        const group = new THREE.Group();
        group.name = "__fixtureGizmoGroup";

        scene.traverse(obj => {
            // ✅ wireMarker'ları hariç tut
            if (obj.userData.role === 'wireMarker') return;

            if (obj.userData.type === 'curvedLine' && selectedObjectIds.includes(obj.userData.id)) {
                const controlPoints = obj.userData.controlPoints;
                if (controlPoints) {
                    controlPoints.forEach((pt, index) => {
                        // Skip first vertex
                        if (index === 0) return;

                        const geometry = new THREE.PlaneGeometry(4, 4);
                        const material = new THREE.MeshBasicMaterial({ color: 0xff00ff, depthTest: false, transparent: true, opacity: 0.8 }); // Magenta for fixture handles
                        const gizmo = new THREE.Mesh(geometry, material);
                        gizmo.position.copy(pt);
                        gizmo.name = `__fixtureHandle_${index}`;
                        gizmo.userData = {
                            isFixtureHandle: true,
                            wireId: obj.userData.id,
                            index: index
                        };
                        group.add(gizmo);
                    });
                }
            }
        });

        scene.add(group);
        gizmoGroupRef.current = group;

        return () => {
            if (gizmoGroupRef.current) {
                scene.remove(gizmoGroupRef.current);
            }
        };
    }, [selectedObjectIds, scene, wires]); // Re-render when selection changes

    // 2. Interaction Logic
    useEffect(() => {
        if (!domElement || !camera || !scene) return;

        const getMouse = (event) => {
            const rect = domElement.getBoundingClientRect();
            return {
                x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
                y: -((event.clientY - rect.top) / rect.height) * 2 + 1,
            };
        };

        const onClick = (event) => {
            const mouse = getMouse(event);
            raycaster.current.setFromCamera(mouse, camera);
            
            if (!isMoving.current) {
                // Try to pick a handle
                const intersects = raycaster.current.intersectObjects(scene.children, true);
                const hit = intersects.find(i => i.object.userData.isFixtureHandle);
                
                if (hit) {
                    isMoving.current = true;
                    selectedGizmo.current = hit.object;
                    
                    // Setup drag plane
                    const normal = new THREE.Vector3();
                    camera.getWorldDirection(normal);
                    plane.current.setFromNormalAndCoplanarPoint(normal, hit.object.position);
                    
                    // Calculate offset
                    const intersectPoint = new THREE.Vector3();
                    raycaster.current.ray.intersectPlane(plane.current, intersectPoint);
                    offset.current.subVectors(hit.object.position, intersectPoint);
                    
                    event.stopPropagation(); // Prevent other clicks?
                }
            } else {
                // Stop moving
                isMoving.current = false;
                selectedGizmo.current = null;
            }
        };

        const onMouseMove = (event) => {
            if (!isMoving.current || !selectedGizmo.current) return;

            const mouse = getMouse(event);
            raycaster.current.setFromCamera(mouse, camera);
            
            const intersectPoint = new THREE.Vector3();
            if (raycaster.current.ray.intersectPlane(plane.current, intersectPoint)) {
                const newPos = intersectPoint.add(offset.current);
                selectedGizmo.current.position.copy(newPos);
                
                // Update Wire Geometry
                const { wireId, index } = selectedGizmo.current.userData;
                let wireObj = null;
                scene.traverse(o => {
                    if (o.userData.id === wireId && o.userData.type === 'curvedLine') {
                        wireObj = o;
                    }
                });

                if (wireObj && wireObj.userData.controlPoints) {
                    // Update control point
                    wireObj.userData.controlPoints[index].copy(newPos);
                    
                    // Regenerate Curve
                    const curve = new THREE.CatmullRomCurve3(wireObj.userData.controlPoints);
                    const divisions = Math.max(10, wireObj.userData.controlPoints.length * 10);
                    const points = curve.getPoints(divisions);
                    wireObj.geometry.setFromPoints(points);
                    
                    // Update Wire Markers (if any) - Optional for now
                }
            }
        };

        domElement.addEventListener('click', onClick);
        window.addEventListener('mousemove', onMouseMove);

        return () => {
            domElement.removeEventListener('click', onClick);
            window.removeEventListener('mousemove', onMouseMove);
        };
    }, [domElement, camera, scene]);
}
